// components/AddPlatformUsersModal.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiSearch, FiCheck, FiUser, FiAtSign } from 'react-icons/fi';
import supabase from '../services/supabase';
import '../styles/Modals.scss';

const AddPlatformUsersModal = ({ isOpen, onClose, guestListId, onUsersAdded, existingContactIds = [] }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);

  const handleSearch = async (query) => {
    setSearchQuery(query);

    if (query.length < 2) {
      setUsers([]);
      setHasMore(false);
      return;
    }

    try {
      setSearching(true);
      setPage(0);

      const limit = 10;
      const from = 0;
      const to = from + limit - 1;

      const { data, error, count } = await supabase
        .from('users')
        .select('id, first_name, last_name, username, profile_photo_cdn_url, phone_number', { count: 'exact' })
        .or(`username.ilike.%${query}%,first_name.ilike.%${query}%,last_name.ilike.%${query}%`)
        .range(from, to);

      if (error) throw error;

      // Filter out users already in guest list
      const filteredUsers = data.filter(
        user => !existingContactIds.includes(user.id)
      );

      setUsers(filteredUsers);
      setHasMore(to < count - 1);
    } catch (error) {
      console.error('Error searching users:', error);
      alert('Failed to search users');
    } finally {
      setSearching(false);
    }
  };

  const handleLoadMore = async () => {
    if (!hasMore || searching) return;

    try {
      setSearching(true);
      const nextPage = page + 1;
      const limit = 10;
      const from = nextPage * limit;
      const to = from + limit - 1;

      const { data, error, count } = await supabase
        .from('users')
        .select('id, first_name, last_name, username, profile_photo_cdn_url, phone_number', { count: 'exact' })
        .or(`username.ilike.%${searchQuery}%,first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%`)
        .range(from, to);

      if (error) throw error;

      // Filter out users already in guest list
      const filteredUsers = data.filter(
        user => !existingContactIds.includes(user.id)
      );

      setUsers([...users, ...filteredUsers]);
      setHasMore(to < count - 1);
      setPage(nextPage);
    } catch (error) {
      console.error('Error loading more users:', error);
    } finally {
      setSearching(false);
    }
  };

  const toggleUser = (user) => {
    const isSelected = selectedUsers.some((u) => u.id === user.id);

    if (isSelected) {
      setSelectedUsers(selectedUsers.filter((u) => u.id !== user.id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const handleSave = async () => {
    if (selectedUsers.length === 0) {
      alert('Please select at least one user');
      return;
    }

    try {
      setSaving(true);

      // Format users for guest list
      const contacts = selectedUsers.map((user) => ({
        guest_list_id: guestListId,
        name: `${user.first_name} ${user.last_name}`.trim(),
        phone_number: user.phone_number || null,
        user_id: user.id,
        created_at: new Date().toISOString(),
      }));

      // Add to guest list
      const { error } = await supabase
        .from('guest_list_contacts')
        .insert(contacts);

      if (error) throw error;

      alert(`Added ${selectedUsers.length} ${selectedUsers.length === 1 ? 'user' : 'users'} to guest list`);
      
      // Reset and close
      setSelectedUsers([]);
      setSearchQuery('');
      setUsers([]);
      onUsersAdded();
    } catch (error) {
      console.error('Error adding users:', error);
      alert('Failed to add users to guest list');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedUsers([]);
    setSearchQuery('');
    setUsers([]);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="modal-overlay" onClick={handleClose}>
          <motion.div
            className="modal-content add-users-modal"
            initial={{ opacity: 0, scale: 0.9, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 50 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="modal-header">
              <h3>Add Platform Users</h3>
              <button className="close-btn" onClick={handleClose}>
                <FiX />
              </button>
            </div>

            {/* Search */}
            <div className="modal-search">
              <FiSearch />
              <input
                type="text"
                placeholder="Search by username or name..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                autoFocus
              />
            </div>

            {/* Body */}
            <div className="modal-body users-list">
              {searchQuery.length < 2 ? (
                <div className="instructions">
                  <FiSearch size={48} />
                  <p>Search for users by username or name</p>
                  <span>Enter at least 2 characters to start searching</span>
                </div>
              ) : searching && users.length === 0 ? (
                <div className="loading">
                  <div className="spinner"></div>
                  <p>Searching...</p>
                </div>
              ) : users.length > 0 ? (
                <>
                  {users.map((user) => {
                    const isSelected = selectedUsers.some((u) => u.id === user.id);
                    const displayName = `${user.first_name} ${user.last_name}`.trim();

                    return (
                      <div
                        key={user.id}
                        className={`user-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleUser(user)}
                      >
                        <div className="user-info">
                          {user.profile_photo_cdn_url ? (
                            <img 
                              src={user.profile_photo_cdn_url} 
                              alt={displayName}
                              className="user-avatar"
                            />
                          ) : (
                            <div className="user-avatar placeholder">
                              {displayName.charAt(0).toUpperCase()}
                            </div>
                          )}

                          <div className="user-details">
                            <h4>{displayName}</h4>
                            <div className="username">
                              <FiAtSign size={12} />
                              <span>{user.username}</span>
                            </div>
                          </div>
                        </div>

                        <div className="checkbox">
                          {isSelected && <FiCheck />}
                        </div>
                      </div>
                    );
                  })}

                  {hasMore && (
                    <button
                      className="btn btn-load-more"
                      onClick={handleLoadMore}
                      disabled={searching}
                    >
                      {searching ? (
                        <div className="spinner small"></div>
                      ) : (
                        <span>Load More</span>
                      )}
                    </button>
                  )}
                </>
              ) : (
                <div className="empty">
                  <FiUser size={48} />
                  <p>No users found</p>
                  <span>Try a different search term</span>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={handleClose}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleSave}
                disabled={selectedUsers.length === 0 || saving}
              >
                {saving ? (
                  <div className="spinner small"></div>
                ) : (
                  <>
                    <FiCheck />
                    <span>Add ({selectedUsers.length})</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AddPlatformUsersModal;